import { useEffect, useRef } from 'react'
import { ConnectionState, DisconnectReason, Room, RoomEvent } from 'livekit-client'
import { isCandidateForQuestionSession } from '@/src/lib/companyParticipant'
import { clientLogWarn } from '@/src/lib/clientLog'
import {
  beginCandidateReconnect, CANDIDATE_RECONNECT_TOPIC, finishCandidateReconnect,
  type CandidateReconnectInterval,
  type CandidateReconnectPacket,
} from '@/src/lib/candidateReconnectInterval'

interface Options {
  room: Room
  enabled: boolean
}

const NETWORK_DISCONNECT_REASONS = new Set([
  DisconnectReason.UNKNOWN_REASON, DisconnectReason.STATE_MISMATCH, DisconnectReason.JOIN_FAILURE,
  DisconnectReason.SIGNAL_CLOSE, DisconnectReason.CONNECTION_TIMEOUT, DisconnectReason.MEDIA_FAILURE,
])
const MAX_DELIVERY_ATTEMPTS = 3

/** Records short SDK interruptions even when LiveKit keeps the participant in the room. */
export function useCandidateReconnectTelemetry({room, enabled}: Options) {
  // ActiveRoom can replace its Room instance during a manual retry. Keep the original SID and start.
  const intervalRef = useRef<CandidateReconnectInterval | null>(null)
  const knownSidRef = useRef<string | undefined>()
  const pendingRef = useRef(new Map<string, {packet: CandidateReconnectPacket; attempts: number}>())

  useEffect(() => {
    if (!enabled) {
      intervalRef.current = null
      knownSidRef.current = undefined
      pendingRef.current.clear()
      return
    }
    let disposed = false
    let finishing = false
    let requestedFinishAt: number | undefined
    let publishing = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    // A fresh Room instance is another delivery opportunity for the same original interval.
    pendingRef.current.forEach(entry => { entry.attempts = 0 })
    const isCandidate = () => {
      const participant = room.localParticipant
      return isCandidateForQuestionSession(participant.attributes)
        && !participant.isAgent && !participant.permissions?.agent
        && !participant.permissions?.recorder && !participant.permissions?.hidden
    }

    const onReconnecting = () => {
      if (!isCandidate()) return
      if (!intervalRef.current) pendingRef.current.forEach(entry => { entry.attempts = 0 })
      intervalRef.current = beginCandidateReconnect(intervalRef.current, Date.now(), knownSidRef.current, crypto.randomUUID())
    }

    const onDisconnected = (reason?: DisconnectReason) => {
      if (reason === undefined || NETWORK_DISCONNECT_REASONS.has(reason)) onReconnecting()
    }

    const sendPending = async (roomSid: string) => {
      if (publishing || disposed || room.state !== ConnectionState.Connected || !isCandidate()) return
      publishing = true
      try {
        for (const [id, entry] of pendingRef.current) {
          if (disposed || room.state !== ConnectionState.Connected || !isCandidate()) break
          // Re-anchor delivery time on every attempt; the original gap boundaries and ID stay unchanged.
          const packet = finishCandidateReconnect(entry.packet, roomSid, entry.packet.finishedAt, Date.now())
          if (!packet) {
            pendingRef.current.delete(id)
            continue
          }
          if (entry.attempts >= MAX_DELIVERY_ATTEMPTS) continue
          entry.attempts += 1
          try {
            await room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(packet)), {
              reliable: true, topic: CANDIDATE_RECONNECT_TOPIC,
            })
            if (pendingRef.current.get(id) === entry) pendingRef.current.delete(id)
          } catch {
            if (!disposed) clientLogWarn('candidate reconnect telemetry could not be delivered')
          }
        }
      } finally {
        publishing = false
        if (!disposed && room.state === ConnectionState.Connected && isCandidate()
          && [...pendingRef.current.values()].some(entry => entry.attempts < MAX_DELIVERY_ATTEMPTS)) {
          clearTimeout(retryTimer)
          retryTimer = setTimeout(() => { retryTimer = undefined; void sendPending(roomSid) }, 500)
        }
      }
    }

    const onConnected = async () => {
      if (!isCandidate()) return
      if (finishing) {
        requestedFinishAt ??= Date.now()
        return
      }
      finishing = true
      const interval = intervalRef.current
      const finishedAt = requestedFinishAt ?? Date.now()
      requestedFinishAt = undefined
      let readySid: string | undefined
      try {
        const roomSid = await room.getSid()
        if (disposed || room.state !== ConnectionState.Connected || !isCandidate()) return
        knownSidRef.current = roomSid
        readySid = roomSid
        // A parallel Connected/Reconnected callback or a newer interruption must not consume this twice.
        if (interval && intervalRef.current === interval) {
          intervalRef.current = null
          const packet = finishCandidateReconnect(interval, roomSid, finishedAt, Date.now())
          if (packet) pendingRef.current.set(packet.id, {packet, attempts: 0})
        }
      } catch {
        if (!disposed) clientLogWarn('candidate reconnect telemetry could not be delivered')
      } finally {
        finishing = false
        if (!disposed && requestedFinishAt !== undefined && room.state === ConnectionState.Connected) void onConnected()
      }
      if (readySid) await sendPending(readySid)
    }

    room.on(RoomEvent.Reconnecting, onReconnecting)
    // Signal-only resumption can leave media flowing and must not remove candidate speech.
    room.on(RoomEvent.Disconnected, onDisconnected)
    room.on(RoomEvent.Reconnected, onConnected)
    room.on(RoomEvent.Connected, onConnected)
    if (room.state === ConnectionState.Connected) void onConnected()
    else if (room.state === ConnectionState.Reconnecting) onReconnecting()
    return () => {
      disposed = true
      clearTimeout(retryTimer)
      room.off(RoomEvent.Reconnecting, onReconnecting)
      room.off(RoomEvent.Disconnected, onDisconnected)
      room.off(RoomEvent.Reconnected, onConnected)
      room.off(RoomEvent.Connected, onConnected)
    }
  }, [room, enabled])
}
